import { ComponentProps, ElementType, FC, ReactNode } from 'react';

import { ArrowRightIcon, CheckIcon, InfoIcon } from '@/assets/icons';
import { cn } from '@/utils/tailwind';

import { Button, Card, IconBox, Skeleton, Tag, Tooltip } from '../../atoms';
import { CardContent } from '../../atoms/Card/Card';

export type SetupItemConnectionPillStatus = 'loading' | 'connected' | 'setup';

type SetupItemProps = {
  icon: ElementType;
  connectionPillStatus: SetupItemConnectionPillStatus;
  connectedPillLabel?: string;
  setupPillLabel: string;
  title: string;
  description: string;
  buttonLabel: string;
  tooltipTitle: ReactNode;
  onClick: () => void;
  /** When true, the action button is disabled (e.g. step already completed). */
  isButtonDisabled?: boolean;
  className?: string;
} & ComponentProps<'div'>;

const PILL_SLOT_MIN_WIDTH = 'min-w-[7.5rem]';

export const SetupItem: FC<SetupItemProps> = ({
  title,
  description,
  icon,
  buttonLabel,
  tooltipTitle,
  connectionPillStatus,
  connectedPillLabel = 'Connected',
  setupPillLabel,
  className,
  onClick,
  isButtonDisabled = false,
  ...props
}) => {
  const isConnected = connectionPillStatus === 'connected';

  return (
    <Card
      {...props}
      className={cn('rounded-[12px] p-4', { 'border-custom-cyan-400': !isConnected }, className)}
    >
      <CardContent className="flex flex-col min-h-[235px]">
        <div className="flex items-start justify-between gap-3 mb-3">
          <IconBox size="large" variant="default" icon={icon} className="text-custom-cyan-500" />
          <div
            className={cn('flex shrink-0 items-center justify-end self-start', PILL_SLOT_MIN_WIDTH)}
            aria-busy={connectionPillStatus === 'loading'}
          >
            {connectionPillStatus === 'loading' && (
              <Skeleton className="h-[22px] w-full max-w-[7.5rem] rounded-[5px]" />
            )}
            {connectionPillStatus === 'connected' && (
              <Tag
                className="!leading-[1.6]"
                size="regular"
                color="cyan"
                label={connectedPillLabel}
                leftIcon={<CheckIcon width={16} height={16} />}
              />
            )}
            {connectionPillStatus === 'setup' && (
              <Tag className="!leading-[1.6]" size="regular" color="grey" label={setupPillLabel} />
            )}
          </div>
        </div>
        <div className="grow">
          <div className="flex items-center gap-2 mb-2">
            <p className="text-body-16-medium text-black">{title}</p>
            <Tooltip title={tooltipTitle}>
              <InfoIcon width={20} height={20} />
            </Tooltip>
          </div>
          <div>
            <p className="text-body-14-regular text-custom-warm-grey-600">{description}</p>
          </div>
        </div>
        <div>
          <Button
            onClick={onClick}
            variant="secondary"
            size="lg"
            disabled={isButtonDisabled}
            rightIcon={<ArrowRightIcon width={20} height={20} />}
          >
            {buttonLabel}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
